import { useId, useState, type FocusEvent, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { StickyNote } from "lucide-react";

interface NotesPreviewProps {
  notes: string;
  /** What the notes belong to, for the accessible name ("Notes for Carlos Ortiz"). */
  label: string;
  children?: never;
}

interface PanelPosition {
  placement: "below" | "above";
  /** Distance from the viewport edge the panel is pinned to (top for below, bottom for above). */
  offset: number;
  right: number;
}

const PANEL_WIDTH = 288;
const PANEL_MAX_HEIGHT = 200;
const GAP = 4;

/**
 * Notes icon for dense grid rows. Hovering or focusing it shows the full note
 * text in a panel rendered through a portal, so the preview is never clipped by
 * the grid's scroll container.
 */
export function NotesPreview({ notes, label }: NotesPreviewProps) {
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const panelId = useId();

  const show = (event: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const fitsBelow = rect.bottom + GAP + PANEL_MAX_HEIGHT < window.innerHeight;
    const right = Math.max(8, window.innerWidth - Math.max(rect.right, PANEL_WIDTH + 8));
    setPosition(fitsBelow ? { placement: "below", offset: rect.bottom + GAP, right } : { placement: "above", offset: window.innerHeight - rect.top + GAP, right });
  };
  const hide = () => setPosition(null);

  return (
    <>
      <button type="button" aria-label={`${label}: ${notes}`} aria-describedby={position ? panelId : undefined} className="inline-flex h-7 w-7 cursor-help items-center justify-center rounded text-gray-500 hover:text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-green-50" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        <StickyNote size={15} aria-hidden="true" />
      </button>
      {position &&
        createPortal(
          <div id={panelId} role="tooltip" className="pointer-events-none fixed z-[60] overflow-hidden whitespace-pre-wrap rounded-lg bg-gray-900 px-3 py-2 text-left text-xs leading-relaxed text-white shadow-lg" style={{ [position.placement === "below" ? "top" : "bottom"]: position.offset, right: position.right, width: PANEL_WIDTH, maxHeight: PANEL_MAX_HEIGHT }}>
            <span className="mb-1 block font-semibold text-gray-300">{label}</span>
            {notes}
          </div>,
          document.body,
        )}
    </>
  );
}
